/**
 * UpsertModuleModal — the Add/Edit Module dialog, extracted from
 * PersonasPage.tsx and generalized so it works for BOTH a persona's own
 * modules and a group's modules (Persona Groups, step 4). It no longer owns
 * a GraphQL mutation directly — the caller supplies `onSubmit`, wired to
 * whichever mutation matches its scope (upsertPersonaModule vs
 * upsertPersonaGroupModule), matching ModuleMatrix's handlers pattern.
 *
 * Deterministic inputs: moduleId is a searchable single-select, actions is a
 * searchable multiselect (module-aware — merges the FE feature catalog with
 * values in use), data scope is structured (requireIpaMatch toggle + line-of-
 * business multiselect). Sort order isn't user-editable here — the caller
 * keeps the existing value on edit and appends new modules after the
 * current max (computed by the caller, passed as `nextSortOrder`).
 */
import { useMemo, useState } from "react";

import SearchableSelect, { type SelectOption } from "./SearchableSelect";
import { catalogActions, FEATURE_CATALOG, type ModuleId } from "../../config/featureCatalog";
import { COLLAPSED_BY_DEFAULT_ACTION, DEFAULT_TAB_ACTION } from "../lib/personaGroupLogic";
import type { AcAdminPersonaModule } from "../graphql/adminOps";

export interface UpsertModuleSubmitVars {
  moduleId: string;
  sortOrder: number;
  actions: string[];
  dataScope?: string;
  isActive: boolean;
}

export default function UpsertModuleModal({
  subjectLabel,
  existingModule,
  nextSortOrder,
  moduleIdOptions,
  actionCatalog,
  lobOptions,
  onClose,
  onSubmit,
}: {
  subjectLabel: string;
  existingModule?: AcAdminPersonaModule;
  nextSortOrder: number;
  moduleIdOptions: SelectOption[];
  actionCatalog: string[];
  lobOptions: SelectOption[];
  onClose: () => void;
  onSubmit: (vars: UpsertModuleSubmitVars) => Promise<unknown>;
}) {
  const [moduleId, setModuleId] = useState<string | null>(existingModule?.moduleId ?? null);
  // The COLLAPSED_BY_DEFAULT_ACTION/DEFAULT_TAB_ACTION sentinels are NOT real
  // actions — kept OUT of this list (their own controls own them, back on the
  // matrix row) and re-merged on submit so an existing module's stored value
  // doesn't leak into the Actions multiselect.
  const [actions, setActions] = useState<string[]>(
    (existingModule?.actions ?? []).filter((a) => a !== COLLAPSED_BY_DEFAULT_ACTION && a !== DEFAULT_TAB_ACTION),
  );
  const hadDefaultTabFlag = (existingModule?.actions ?? []).includes(DEFAULT_TAB_ACTION);
  const [expandedByDefault, setExpandedByDefault] = useState<boolean>(
    !(existingModule?.actions ?? []).includes(COLLAPSED_BY_DEFAULT_ACTION),
  );
  const [requireIpaMatch, setRequireIpaMatch] = useState<boolean>(existingModule?.dataScope?.requireIpaMatch ?? false);
  const [allowedLob, setAllowedLob] = useState<string[]>(existingModule?.dataScope?.allowedLineOfBusiness ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const actionOptions: SelectOption[] = useMemo(() => {
    const set = new Set<string>(actionCatalog);
    if (moduleId && moduleId in FEATURE_CATALOG) {
      for (const a of catalogActions(moduleId as ModuleId)) set.add(a);
    }
    for (const a of actions) set.add(a);
    set.delete(COLLAPSED_BY_DEFAULT_ACTION);
    set.delete(DEFAULT_TAB_ACTION);
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((a) => ({ value: a, label: a }));
  }, [actionCatalog, moduleId, actions]);

  const showExpandedToggle = moduleId === "HomeDemographics";

  const submit = async () => {
    if (!moduleId) return;
    const hasScope = requireIpaMatch || allowedLob.length > 0;
    const dataScope =
      existingModule || hasScope
        ? JSON.stringify({
            requireIpaMatch,
            ...(allowedLob.length > 0 ? { allowedLineOfBusiness: allowedLob } : {}),
          })
        : undefined;
    let outActions = showExpandedToggle && !expandedByDefault ? [...actions, COLLAPSED_BY_DEFAULT_ACTION] : actions;
    if (hadDefaultTabFlag) outActions = [...outActions, DEFAULT_TAB_ACTION];

    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        moduleId,
        sortOrder: existingModule?.sortOrder ?? nextSortOrder,
        actions: outActions,
        dataScope,
        isActive: existingModule?.isActive ?? true,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-bold mb-1">{existingModule ? "Edit Module" : "Add Module"}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {subjectLabel}
        </p>
        <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Module <span className="text-destructive">*</span>
            </label>
            <SearchableSelect
              value={moduleId}
              onChange={setModuleId}
              options={moduleIdOptions}
              disabled={!!existingModule}
              ariaLabel="Module"
              placeholder="Select a module…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Actions</label>
            <SearchableSelect
              multiple
              value={actions}
              onChange={setActions}
              options={actionOptions}
              allowCustom
              ariaLabel="Actions"
              placeholder="Add actions…"
            />
          </div>

          {showExpandedToggle && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={expandedByDefault}
                onChange={(e) => setExpandedByDefault(e.target.checked)}
                className="rounded border-input"
              />
              <span className="font-medium">Expanded by default</span>
              <span className="text-xs text-muted-foreground">(off = card starts collapsed)</span>
            </label>
          )}

          <fieldset className="rounded-lg border border-border p-3">
            <legend className="text-sm font-medium px-1">Data Scope</legend>
            <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requireIpaMatch}
                onChange={(e) => setRequireIpaMatch(e.target.checked)}
                className="rounded border-input"
              />
              Require IPA match
            </label>
            <label className="block text-sm font-medium mb-1">Line of business</label>
            <SearchableSelect
              multiple
              value={allowedLob}
              onChange={setAllowedLob}
              options={lobOptions}
              allowCustom
              ariaLabel="Allowed line of business"
              placeholder="Any (unrestricted)…"
            />
          </fieldset>

          {error && <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !moduleId}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
